import { createWebHistory, createRouter } from 'vue-router';
import { checkLogin, checkToken } from '@/scripts/token';
import tokenRefreshService from '@/scripts/tokenRefreshService.js';

import constants from '@/constants';
import store from '@/store/index.js';

import MainPage from '@/views/MainPage.vue';
import AuthorizationPage from '@/views/AuthorizationPage.vue';
import FileEditPage from '@/views/FileEditPage.vue';
import UserPage from '@/views/UserPage.vue';
import GupUploadsPage from '@/views/GupUploadsPage.vue';
import ProviderAndGamePage from '@/views/ProviderAndGamePage.vue';
import TagPage from '@/views/TagPage.vue';
import SettingPage from '@/views/SettingPage.vue';
import ChatGPTPage from '@/views/ChatGPTPage.vue';
import FilterPage from '@/views/FilterPage.vue';
import ChangeLogPage from '@/views/ChangeLogPage.vue';
import AssetTypePage from '@/views/AssetTypePage.vue';
import SmartFilterPage from '@/views/SmartFilterPage.vue';
import TestEmailsPage from '@/views/TestEmailsPage.vue';
import AntifraudPage from '@/views/AntifraudPage.vue';
import BillingPage from '@/views/BillingPage.vue';
import CorporateRequestsPage from '@/views/CorporateRequestsPage.vue';
import UserAnalyticsPage from '@/views/UserAnalyticsPage.vue';
import PublisherMaterialRequestsAdminPage from '@/views/PublisherMaterialRequestsAdminPage.vue';
import ProviderManagementPage from '@/views/ProviderManagementPage.vue';
import PartnersPage from '@/views/PartnersPage.vue';
import SystemAnalyticsPage from '@/views/SystemAnalyticsPage.vue';
// [FIX] TestLandingPage is a dev/QA-only page — never ship it in production builds.
// VUE_APP_ENABLE_TEST_LANDING=true lets the dev server (admin-dev) opt in even
// when NODE_ENV=production (production build, non-production deployment).
const TEST_LANDING_ENABLED = process.env.NODE_ENV !== 'production' || process.env.VUE_APP_ENABLE_TEST_LANDING === 'true';
const TestLandingPage = TEST_LANDING_ENABLED
    ? require('@/views/TestLandingPage.vue').default
    : null;
import SystemSettingsPage from '@/views/SystemSettingsPage.vue';

const routes = [
    {
        path: '/',
        name: constants.ROUTER_NAME.MAIN,
        component: MainPage,
    },
    {
        path: '/auth',
        name: constants.ROUTER_NAME.AUTHORIZATION,
        component: AuthorizationPage,
    },
    {
        path: '/file-edit',
        name: constants.ROUTER_NAME.FILEEDIT,
        component: FileEditPage,
    },
    {
        path: '/user-edit',
        name: constants.ROUTER_NAME.USEREDIT,
        component: UserPage,
    },
    {
        path: '/user-edit/:userId',
        name: 'userEditWithId',
        component: UserPage,
    },
    {
        path: '/provider-and-game-edit',
        name: constants.ROUTER_NAME.PROVIDERANDGAMEEDIT,
        component: ProviderAndGamePage,
    },
    {
        path: '/gup-uploads',
        name: constants.ROUTER_NAME.GUPUPLOADS,
        component: GupUploadsPage,
    },
    {
        path: '/tag-edit',
        name: constants.ROUTER_NAME.TAGEDIT,
        component: TagPage,
    },
    {
        path: '/chat-GPT-edit',
        name: constants.ROUTER_NAME.CHATGPTEDIT,
        component: ChatGPTPage,
    },
    {
        path: '/filter-edit',
        name: constants.ROUTER_NAME.FILTEREDIT,
        component: FilterPage,
    },
    {
        path: '/asset-type-edit',
        name: constants.ROUTER_NAME.ASSETTYPE,
        component: AssetTypePage,
    },
    {
        path: '/smart-filter-edit',
        name: constants.ROUTER_NAME.SMARTFILTER,
        component: SmartFilterPage,
    },
    {
        path: '/setting',
        name: constants.ROUTER_NAME.SETTING,
        component: SettingPage,
    },
    {
        path: '/change-log',
        name: constants.ROUTER_NAME.CHANGELOG,
        component: ChangeLogPage,
    },
    {
        path: '/test-emails',
        name: constants.ROUTER_NAME.TESTEMAILS,
        component: TestEmailsPage,
        beforeEnter: async (to, from, next) => {
            // Allow access in mock mode (email_sending_enabled=false) OR dual mode
            // (test_storage_enabled=true) — backend exposes test_emails_available
            const emailMode = await store.dispatch('LOAD_EMAIL_MODE');
            if (store.getters.IS_TEST_EMAILS_AVAILABLE) {
                next();
            } else {
                next({ name: constants.ROUTER_NAME.MAIN });
            }
        },
    },
    {
        path: '/antifraud',
        name: constants.ROUTER_NAME.ANTIFRAUD,
        component: AntifraudPage,
    },
    {
        path: '/billing',
        name: constants.ROUTER_NAME.BILLING,
        component: BillingPage,
    },
    {
        path: '/corporate-requests',
        name: constants.ROUTER_NAME.CORPORATEREQUESTS,
        component: CorporateRequestsPage,
    },
    {
        path: '/user-analytics',
        name: constants.ROUTER_NAME.USERANALYTICS,
        component: UserAnalyticsPage,
    },
    {
        path: '/user-analytics/:userId',
        name: 'userAnalyticsWithUser',
        component: UserAnalyticsPage,
    },
    {
        path: '/system-analytics',
        name: constants.ROUTER_NAME.SYSTEMANALYTICS,
        component: SystemAnalyticsPage,
    },
    {
        path: '/system-settings',
        name: constants.ROUTER_NAME.SYSTEMSETTINGS,
        component: SystemSettingsPage,
    },
    {
        path: '/publisher-material-requests',
        name: constants.ROUTER_NAME.PUBLISHER_MATERIAL_REQUESTS,
        component: PublisherMaterialRequestsAdminPage,
        beforeEnter: async () => {
            await checkToken();
            const permissions = store.getters.USER_PERMISSIONS || {};
            if (!permissions.can_manage_publishers) {
                return { name: constants.ROUTER_NAME.MAIN };
            }
        },
    },
    {
        path: '/provider-management',
        name: constants.ROUTER_NAME.PROVIDER_MANAGEMENT,
        component: ProviderManagementPage,
        beforeEnter: async () => {
            await checkToken();
            const permissions = store.getters.USER_PERMISSIONS || {};
            if (!permissions.can_manage_publishers) {
                return { name: constants.ROUTER_NAME.MAIN };
            }
        },
    },
    {
        path: '/partners',
        name: constants.ROUTER_NAME.PARTNERS,
        component: PartnersPage,
        beforeEnter: async () => {
            await checkToken();
            const permissions = store.getters.USER_PERMISSIONS || {};
            if (!permissions.can_use_admin) {
                return { name: constants.ROUTER_NAME.MAIN };
            }
        },
    },
    // [FIX] Test landing page — registered only when TEST_LANDING_ENABLED.
    ...(TEST_LANDING_ENABLED ? [{
        path: '/landing',
        name: constants.ROUTER_NAME.TEST_LANDING,
        component: TestLandingPage,
        meta: { public: true },
    }] : []),
];

const router = createRouter({
    history: createWebHistory(),
    routes,
});

router.beforeEach(async (to, from) => {
    console.debug('[router] beforeEach to.name:', to.name, 'to.path:', to.path, 'meta.public:', to.meta?.public);

    // Public routes — no auth required
    if (to.meta?.public) {
        tokenRefreshService.stop();
        return;
    }

    // Handle token refresh service based on route
    if (to.name === constants.ROUTER_NAME.AUTHORIZATION) {
        // Stop token refresh when going to auth page
        tokenRefreshService.stop();
        return;
    }

    // Start token refresh when leaving auth page (if not already running)
    if (!tokenRefreshService.isRunning()) {
        tokenRefreshService.start();
    }

    // Сброс параметров глобального поиска
    if (to.name === constants.ROUTER_NAME.MAIN) {
        store.dispatch('RESET_PARAMS');
    }

    // Проверка что пользователь авторизован
    const loginState = await checkLogin();
    if (loginState === true) {
        await checkToken();
    } else {
        // Unauthenticated visitor arriving at / with a partner ?ref= param — forward
        // to the public landing page so tracking can work without exposing the admin panel.
        // [FIX] Only redirect to test landing when TEST_LANDING_ENABLED; otherwise the
        // route does not exist so fall through to the normal auth redirect.
        if (TEST_LANDING_ENABLED && to.path === '/' && to.query.ref) {
            console.debug('[router] unauthenticated partner ref visit, forwarding to landing', { ref: to.query.ref });
            return { name: constants.ROUTER_NAME.TEST_LANDING, query: to.query };
        }
        return { name: constants.ROUTER_NAME.AUTHORIZATION };
    }
});

export default router;
